import type { ActivityEvent } from "@productlane/api/src/browser"
import type { ConnectorRunSource } from "@productlane/lib/src/browser"
import { parseThreadActivity } from "@productlane/lib/src/browser"

/** Why the agent ran a connector call, when the row alone does not say. */
export function connectorRunPurpose(source?: ConnectorRunSource): string {
	return source === "ai_draft" ? " for the AI draft" : ""
}

function sharedPurpose(members: ActivityEvent[]): string {
	const purposes = new Set(
		members.map((member) => {
			try {
				const parsed = parseThreadActivity(
					member.threadActivity.eventType,
					member.threadActivity.payload,
				)
				return parsed.eventType === "custom_data_action_run"
					? connectorRunPurpose(parsed.payload.source)
					: ""
			} catch {
				return ""
			}
		}),
	)
	return purposes.size === 1 ? [...purposes][0]! : ""
}

function failureCount(members: ActivityEvent[]): number {
	return members.filter((member) => {
		try {
			const parsed = parseThreadActivity(
				member.threadActivity.eventType,
				member.threadActivity.payload,
			)
			return (
				parsed.eventType === "custom_data_action_run" && !parsed.payload.success
			)
		} catch {
			return false
		}
	}).length
}

export function buildAgentBurstLabel(members: ActivityEvent[]): string {
	const count = members.length
	const ranEverything = members.every(
		(member) => member.threadActivity.eventType === "custom_data_action_run",
	)
	const noun = count === 1 ? "action" : "actions"
	const label = ranEverything
		? `Productlane Agent ran ${count} ${noun}${sharedPurpose(members)}`
		: `Productlane Agent made ${count} ${count === 1 ? "change" : "changes"}`

	const failed = failureCount(members)
	return failed > 0 ? `${label}, ${failed} failed` : label
}

/** The affordance on the collapsed row: what expanding it reveals. */
export function buildAgentBurstExpandLabel(members: ActivityEvent[]): string {
	const failed = failureCount(members)
	if (failed > 0) {
		return `Show ${failed} failed ${failed === 1 ? "run" : "runs"}`
	}
	const count = members.length
	return `Show ${count} ${count === 1 ? "run" : "runs"}`
}
